import { Box, Text } from '@mantine/core';
import type { SignatureInfo } from '../pages/appointments/fhirNoteMapping';

const CURSIVE_FONT = "'Brush Script MT', 'Segoe Script', 'Lucida Handwriting', cursive";

const SIGNATURE_BOX_STYLE = {
  backgroundColor: 'var(--mantine-color-blue-0)',
  borderRadius: 'var(--mantine-radius-md)',
  minHeight: 120,
} as const;

function formatSignatureDateTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

export interface SignatureDisplayBoxProps {
  signature: SignatureInfo | null | undefined;
  label: string;
  emptyLabel?: string;
  /** Stretch to fill parent height (e.g. side-by-side grid columns). */
  fullHeight?: boolean;
}

function signatureBoxStyle(fullHeight?: boolean) {
  return {
    ...SIGNATURE_BOX_STYLE,
    ...(fullHeight ? { flex: 1, width: '100%' } : {}),
  };
}

/** Read-only signature block — same styling as client portal signature section. */
export function SignatureDisplayBox({
  signature,
  label,
  emptyLabel = 'Not yet signed',
  fullHeight,
}: SignatureDisplayBoxProps): JSX.Element {
  const boxStyle = signatureBoxStyle(fullHeight);

  if (!signature?.name?.trim() && !signature?.signatureData?.trim()) {
    return (
      <Box p="md" style={boxStyle}>
        <Text size="sm" c="gray.6" mb={6}>
          {label}
        </Text>
        <Text size="sm" c="gray.5" fs="italic">
          {emptyLabel}
        </Text>
      </Box>
    );
  }

  return (
    <Box p="md" style={boxStyle}>
      <Text size="sm" c="gray.6" mb={6}>
        {label}
      </Text>
      {signature.signatureData ? (
        <img
          src={`data:${signature.signatureFormat ?? 'image/png'};base64,${signature.signatureData}`}
          alt={`Signature of ${signature.name}`}
          style={{ maxHeight: 60, maxWidth: '100%', objectFit: 'contain', display: 'block' }}
        />
      ) : (
        <Text size="xl" fw={600} c="gray.8" style={{ fontFamily: CURSIVE_FONT }}>
          {signature.name}
        </Text>
      )}
      {signature.license && (
        <Text size="md" c="gray.7" mt={6} style={{ whiteSpace: 'pre-line' }}>
          {signature.license}
        </Text>
      )}
      {signature.title && (
        <Text size="sm" c="gray.7" mt={4}>
          {signature.title}
        </Text>
      )}
      {signature.signedAt && (
        <Text size="sm" c="gray.6" mt={6}>
          {formatSignatureDateTime(signature.signedAt)}
        </Text>
      )}
    </Box>
  );
}
